import React, { useState, useEffect } from 'react';
import { ProductCard } from './ProductCard';
import { Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const RecentlyViewed = () => {
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentProducts = async () => {
      try {
        // Get recently viewed product IDs from localStorage
        const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
        
        if (recentlyViewed.length === 0) {
          setLoading(false);
          return;
        }

        // Fetch product details for recently viewed items
        const productPromises = recentlyViewed.slice(0, 8).map(async (productId) => {
          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`);
            if (response.ok) {
              return await response.json();
            }
            return null;
          } catch {
            return null;
          }
        });

        const products = (await Promise.all(productPromises)).filter(Boolean);
        setRecentProducts(products);
      } catch (error) {
        console.error('Failed to fetch recently viewed:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentProducts();
  }, []);

  // Don't show section if no recent products
  if (loading || recentProducts.length === 0) {
    return null;
  }

  return (
    <section className="py-5 sm:py-7" data-testid="recently-viewed-section">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg sm:text-xl font-heading font-semibold">Recently Viewed</h3>
          </div>
          <Link to="/products" className="text-sm text-primary hover:underline flex items-center gap-1">
            Browse more
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div 
          className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="recently-viewed-rail"
        >
          {recentProducts.map((product) => (
            <div key={product.id} className="w-[160px] sm:w-[190px] flex-shrink-0 snap-start">
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Helper function to track product views
export const trackProductView = (productId) => {
  try {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    
    // Remove if already exists (to move to front)
    const filtered = recentlyViewed.filter(id => id !== productId);
    
    // Add to beginning
    filtered.unshift(productId);
    
    // Keep only last 20 items
    const trimmed = filtered.slice(0, 20);
    
    localStorage.setItem('recentlyViewed', JSON.stringify(trimmed));
  } catch (error) {
    console.error('Failed to track product view:', error);
  }
};
